import { useCallback, useEffect, useRef, useState } from 'react';

import { streamChat, type ChatMessage } from '@/api/chat';

/**
 * There is no connection to hold open any more: a turn is one POST that
 * streams its reply and ends. So the status is the state of *this turn*, not
 * of a socket — which is what lets it be honest. The widget it replaced
 * reported "Connected" from a hardcoded true, directly above an error saying
 * no gateway token was configured.
 */
export type ChatStatus = 'ready' | 'thinking' | 'error';

export interface UseChatResult {
  messages: ChatMessage[];
  streaming: { id: string; text: string } | null;
  status: ChatStatus;
  error: { code: string; message: string } | null;
  send: (text: string) => void;
  stop: () => void;
}

/** How much of the conversation is sent back up. The server trims again. */
const HISTORY_TURNS = 12;

function localId(prefix: string): string {
  return `${prefix}-${String(Date.now())}-${Math.random().toString(36).slice(2, 8)}`;
}

export function useChat(): UseChatResult {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [streaming, setStreaming] = useState<{ id: string; text: string } | null>(null);
  const [status, setStatus] = useState<ChatStatus>('ready');
  const [error, setError] = useState<{ code: string; message: string } | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  const liveRef = useRef(true);

  useEffect(() => {
    liveRef.current = true;
    return () => {
      liveRef.current = false;
      abortRef.current?.abort();
    };
  }, []);

  const stop = useCallback(() => {
    abortRef.current?.abort();
  }, []);

  const send = useCallback(
    (text: string) => {
      const message = text.trim();
      if (message === '' || abortRef.current !== null) return;

      setError(null);
      setStatus('thinking');
      const asked: ChatMessage = {
        id: localId('you'),
        role: 'user',
        content: message,
        createdAt: new Date().toISOString(),
      };
      const replyId = localId('ugin');
      setMessages((prev) => [...prev, asked]);
      setStreaming({ id: replyId, text: '' });

      const abort = new AbortController();
      abortRef.current = abort;

      void (async () => {
        let assembled = '';
        let failure: { code: string; message: string } | null = null;
        try {
          const history = [...messages, asked]
            .slice(-HISTORY_TURNS)
            .map((m) => ({ role: m.role, content: m.content }));
          // The turn just asked is the message, not part of the history.
          history.pop();

          for await (const event of streamChat(
            { message, history },
            { signal: abort.signal },
          )) {
            if (event.type === 'delta') {
              assembled += event.text;
              const soFar = assembled;
              setStreaming((prev) => (prev === null ? prev : { ...prev, text: soFar }));
            } else if (event.type === 'error') {
              failure = { code: event.code, message: event.message };
            }
          }
        } finally {
          abortRef.current = null;
          if (liveRef.current) {
            // Whatever arrived is kept, aborted or not: throwing away words
            // already paid for is a second cost on top of the tokens.
            if (assembled !== '') {
              setMessages((prev) => [
                ...prev,
                {
                  id: replyId,
                  role: 'assistant',
                  content: assembled,
                  createdAt: new Date().toISOString(),
                },
              ]);
            }
            setStreaming(null);
            setError(failure);
            setStatus(failure === null ? 'ready' : 'error');
          }
        }
      })();
    },
    [messages],
  );

  return { messages, streaming, status, error, send, stop };
}
